import moment from 'moment';
import React, { useCallback, useContext, useEffect, useRef, useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { generateClient } from '@aws-amplify/api';
import { getSubjectContacts as _getSubjectContacts } from '@/graphql/queries';
import { onUpsertSubjectContact, onUpsertTableChangelog } from '@/graphql/subscriptions';
import Modal from '@/Modal';
import { build_logs_insights_url, outcome_color } from '@/utils';
import RootContext from '@/RootContext';
import './TranscriptModal.scss';

const client = generateClient();

const getSubjectContacts = ({ filters, limit, sorting = { field: 'started_at', order: 'desc' } } = {}) => {
  return client.graphql({ query: _getSubjectContacts, variables: { filters, limit, sorting } })
	.then(response => {
	  return response.data.getSubjectContacts?.subject_contacts
	});
};

export default ({ autoplay = false, id, onClose, transcript }) => {
	const isConstructed = useRef(false);
	const rootContext = useContext(RootContext);
	const auth = rootContext.get('auth');
	const [busy, setBusy] = useState(true);
	const [recordingUrl, setRecordingUrl] = useState();
	const [transcriptUrl, setTranscriptUrl] = useState();
	const subscriptions = useRef([]);
	const inner = useRef();

	const [audioSegments, _setAudioSegments] = useState([]);
	const setAudioSegments = useCallback(items => {
		// CPG-512 Initially just hide tool calls
		_setAudioSegments(items);
	}, []);

	const [subjectContact, _setSubjectContact] = useState();
	const setSubjectContact = useCallback(async sc => {
		console.log('sc', sc);
		let transcript;
		let transcript_json = sc.transcript_json;
		try { transcript_json = JSON.parse(transcript_json) } catch (_) {}
		if (transcript_json && 'transcript' in transcript_json) {
			// Sonic, and preferred transcript format
			transcript = transcript_json.transcript;
		} else if (sc.transcript_url) {
			const response = await fetch(sc.transcript_url);
			const data = await response.json();
			transcript = data?.transcript;
		}
		if (transcript) {
			const data = { results: {} };
			data.results.audio_segments = transcript
				.map(e => {
					return {
						channel_label: e.channel_label,
						start_time: e.start_time,
						transcript: e.text
					};
				});
			setAudioSegments(data.results.audio_segments);
		}
		setTranscriptUrl(sc.transcript_url);
		setRecordingUrl(sc.recording_url);
		_setSubjectContact(sc);
	}, []);

	const fetchRecording = useCallback(async url => {
		// Get a fresh url since they expire in 1 hour
  		let recording_url = url;
		if (!recording_url) recording_url = await client.graphql({ query: 'query GetSubjectContacts($contact_id: [String]) { getSubjectContacts(contact_id: $contact_id) { subject_contacts { recording_url } } }', variables: { contact_id: id, limit: 1 } })
			.then(response => {
	  			return response.data.getSubjectContacts?.subject_contacts[0]?.recording_url;
			});
		setRecordingUrl(recording_url);
	}, []);

	if (!isConstructed.current) {
		isConstructed.current = true;
		if (id) {
			// Subscribe either way. Since we may see additions even if transcript file is available
			subscriptions.current.push(client.graphql({	query: onUpsertSubjectContact, variables: { contact_id: id } }).subscribe({
				next: ({ data }) => {
					// This only has a few fields. It is meant for real-time transcript update only. Do a merge.
					console.log('onUpsertSubjectContact subscription', data);
					setSubjectContact({ ...subjectContact, ...data.onUpsertSubjectContact });
				},
				error: (error) => {
					console.error("Subscription error:", error);
				}
			}));
			subscriptions.current.push(client.graphql({	query: onUpsertTableChangelog, variables: {} }).subscribe({
				next: ({ data }) => {
					const items = data.onUpsertTableChangelog.table_changelog;
					const _items = items.filter(e => e.table_name === 'subject_contacts' && e.table_id === id);
					if (_items.length > 0) {
						// There's at least one update to our subject_contacts
						getSubjectContacts({ filters: { contact_id: { eq: id } } })
							.then(result => {
								const contact = result?.[0];
								setSubjectContact(contact);
								if (contact?.recording_url) {
									fetchRecording(contact.recording_url);
								}
							});
					}
				},
				error: error => {
					console.error('Subscription error:', error);
				}
			}));
		}
		(async () => {
			if (transcript) {
				// Demo purposes
				setAudioSegments(transcript);
				setBusy(false);
			}
			if (id) {
				const result = await getSubjectContacts({ filters: { contact_id: { eq: id } } });
				setSubjectContact(result?.[0]);
				await Promise.allSettled([
					fetchRecording(result?.[0].recording_url)
				]);
				setBusy(false);
			}
		})();
	}

	useEffect(() => {
		return () => {
			subscriptions.current.forEach(e => e.unsubscribe());
		};
	}, []);

	const scrollToCount = useRef(0);
	const scrollToLast = useCallback(() => {
		// Only scrollToLast when still in-progress
		if (inner.current && scrollToCount.current++ > 0) {
			const length = inner.current.childNodes.length;
			if (length > 0) inner.current.childNodes[length - 1].scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
		}
	}, [inner.current]);

	const rows = useMemo(() => {
        const _rows = audioSegments.map((e, i) => {
            const start_time = moment.utc(Math.floor(e.start_time) * 1000).format('mm:ss');
            const end_time = e.end_time ? moment.utc(Math.floor(e.end_time) * 1000).format('mm:ss') : undefined;

            
            if (e.channel_label === 'tool') {
                return (
                  <React.Fragment key={i}>
                    <div className="text-center small text-muted mt-2 mb-1">
                      {start_time}
                      {end_time ? " - " + end_time : ""}
                    </div>
                    <div className="d-flex justify-content-center mb-3">
                      <details
                        className="w-100 text-center"
                        style={{ maxWidth: "85%" }}
                      >
                        <summary
                          className="badge bg-secondary text-wrap p-2 d-inline-flex align-items-center gap-2"
                          style={{ cursor: "pointer", listStyle: "none" }}
                        >
                          <i className="bi bi-gear-fill"></i>{" "}
                          
                          <span>Tool Call</span>
                          <i className="bi bi-chevron-down small"></i>{" "}
                         
                        </summary>
                        <div className="mt-2 text-start p-2 border rounded bg-light">
                          <pre
                            style={{
                              whiteSpace: "pre-wrap",
                              fontSize: "0.75rem",
                              margin: 0,
                              maxHeight: "300px",
                              overflowY: "auto",
                            }}
                          >
                            {e.transcript}
                          </pre>
                        </div>
                      </details>
                    </div>
                  </React.Fragment>
                );
            }

            
            return e.channel_label === 'ch_0' ? (
                <React.Fragment key={i}>
                    <div className="ms-2 small">{start_time}{end_time ? ' - ' + end_time : ''}</div>
                    <div className="d-flex mb-3">
                        <div className="chat-bubble received text-break">
                            {e.transcript}
                        </div>
                    </div>
                </React.Fragment>
            ) : (
                <React.Fragment key={i}>
                    <div className="me-2 small d-flex justify-content-end">{start_time}{end_time ? ' - ' + end_time : ''}</div>
                    <div className="d-flex justify-content-end mb-3">
                        <div className="chat-bubble sent text-break">
                            {e.transcript}
                        </div>
                    </div>
                </React.Fragment>
            );
        });

        
        return (
            <div style={{height: 'calc(100vh - 310px)', overflowY: 'auto'}}>
                <div className="d-flex flex-column ps-2 pe-3" ref={inner}>
                    {subjectContact?.summary && (
                        <div className="mb-3">
                            <div className="border rounded-3 p-2 bg-light-purple text-break">
                                <Markdown
                                    children={subjectContact.summary}
                                    remarkPlugins={[remarkGfm, remarkBreaks]}
                                    components={{
                                        ul: ({ node, ...props }) => (
                                            <ul style={{ margin: 0, paddingLeft: '1.25rem' }} {...props} />
                                        ),
                                        h1: 'h3',
                                        h2: 'h4',
                                        h3: 'h4',
                                        a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                                        code: ({ inline, children }) =>
                                            !inline ? (
                                            <pre style={{ background: "#f6f8fa", padding: "0.75rem", borderRadius: "6px" }}>
                                                <code>{children}</code>
                                            </pre>
                                            ) : (
                                            <code style={{ background: "#eee", padding: "0.2rem 0.4rem", borderRadius: "4px" }}>{children}</code>
                                            )
                                    }}
                                />
                            </div>
                            <div>
                                {(subjectContact.outcomes || []).map((outcome, i) => <span className={`col-auto mt-2 me-2 p-2 badge ${outcome_color(outcome)}`} key={i}>{outcome}</span>)}
                            </div>
                        </div>
                    )}
                    {_rows}
                </div>
            </div>
        );
    }, [audioSegments.length, subjectContact]);

	useEffect(scrollToLast, [audioSegments.length]);

	let started_at;
	if (subjectContact?.started_at) started_at = moment(subjectContact.started_at).format('M/DD/YY h:mm A');
	let d = '';
	if (subjectContact?.duration_seconds) {
		const _d = moment.duration(subjectContact.duration_seconds, 'seconds');
		const h = Math.floor(_d.asHours());
		const m = _d.minutes();
		const s = _d.seconds();
		d = ` ${h > 0 ? String(h).padStart(2, '0') + ':' : ''}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
	}

	const log_url = useMemo(() => {
		const cwStringLiteral = s => String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
		return build_logs_insights_url({
			log_groups: [
				'/aws/lambda/text_chat_openai_addiction',
				'/aws/lambda/text_chat_openai_foxtail',
				'/aws/lambda/text_chat_openai_realtymogul_feedback',
				'/aws/lambda/text_twilio_openai_montana_whitewater',
				'/aws/lambda/text_twilio_openai_qct',
				'/aws/lambda/text_chat_openai_realtymogul',
				'/aws/lambda/text_twilio_openai_realtymogul_winback',
				'/aws/lambda/text_twilio_openai_snow_riders',
				'/aws/lambda/contact_events',
				'/aws/lambda/transcript_ingestion',
			],
			query:
`fields @timestamp, @message
| filter strcontains(@message, "${subjectContact ? cwStringLiteral(subjectContact.contact_id) : ''}")
| sort @timestamp desc
| limit 10000`
		});
	}, [subjectContact]);

	return (
		<Modal onClose={onClose} title={
			<div style={{display: 'flex', justifyContent: 'space-between'}}>
				<span>Transcript ({id})</span>
				{auth?.groups?.includes('admin') && log_url && <a className="me-3" href={log_url} target="_blank">Logs</a>}
				{transcriptUrl && <a className="me-3" href={transcriptUrl}>Download</a>}
			</div>}>
			{busy ? (
				<div className="text-center" style={{marginBottom:'2rem',marginTop:'2rem'}}>
					<button className="btn btn-primary" type="button" disabled>
						<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
						Loading...
					</button>
				</div>
			) : (
				<div>
					<table className="w-100 mx-3 mb-2">
						<thead>
							<th>Date & Time</th>
							<th>Name</th>
							<th>Channel</th>
							<th>City</th>
							<th>Phone Number</th>
						</thead>
						<tbody>
							<tr>
								<td><b>{started_at}</b>{d}</td>
								<td>{subjectContact?.full_name_first}</td>
								<td>{subjectContact?.channel}</td>
								<td>{subjectContact?.city}</td>
								<td>{subjectContact?.phone_number}</td>
							</tr>
						</tbody>
					</table>
					{recordingUrl && (
						<audio autoPlay={autoplay} className="mb-2" controls style={{width: '100%'}}>
							<source src={recordingUrl} />
							Your browser does not support the audio element.
						</audio>
					)}
					{rows}
				</div>
			)}
		</Modal>
	);
};
